import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
    useTheme,
    useMediaQuery,
    Box,
    Stack,
    Typography,
} from "@mui/material";
import Cookies from 'universal-cookie';

import Logo from 'assets/images/ga-logo-sm.png';
import LoadingButton from '@mui/lab/LoadingButton';
import FormControlWrapper from "components/FormControlWrapper";
import CheckboxWrapper from "components/CheckboxWrapper";
import { useTranslation } from "react-i18next";
import { useLoginMutation } from "Services/LoginServices";

import { showErrorToaster } from "store/Shared.Slice";

const setCookieVal = (key, value) => {
    const cookies = new Cookies();
    cookies.set(key, value, { maxAge: 1000000, path: '/', sameSite: true });
}

const LoginForm = ({
    message,
    onLoginSuccess,
    onLoginFailed,
}) => {

    const { t } = useTranslation();
    const theme = useTheme();
    const matchDownMD = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();

    const [userLogin, { isLoading }] = useLoginMutation();

    const [checked, setChecked] = useState(true);
    const [formInput, setFormInput] = useState({
        userNameOrEmail: {
            value: '',
            error: ''
        },
        password: {
            value: '',
            error: ''
        }
    });

    const userNameOrEmail = formInput.userNameOrEmail;
    const password = formInput.password;

    const handleFormInputChange = (field, value) => {
        if (checked) {
            if (field === 'userNameOrEmail') {
                setCookieVal('userNameOrEmail', value);
            }
            if (field === 'password') {
                setCookieVal('password', value);
            }
        }
    
        setCookieVal('remember', checked);
        setFormInput((input) => ({...input, [field]: { value, error: '' }}));
    }

    const handleRemember = (rememberChecked) => {

        if (rememberChecked === false) {
            setCookieVal('userNameOrEmail', '');
            setCookieVal('password', '');
        };
    
        setCookieVal('remember', rememberChecked);
    
        setChecked(rememberChecked);
    }

    const handleLogin = async (event) => {
        event.preventDefault();

        try {
          const model = {
            userNameOrEmail: userNameOrEmail.value,
            password: password.value,
          };
          const response = await userLogin(model).unwrap();
          localStorage.setItem("token", response.token);

          onLoginSuccess?.();

        } catch (error) {
    
          onLoginFailed?.();
            
          // Model Errors
          if (error.data?.errors != null) {
            dispatch(showErrorToaster(t("errors.model_error")));
            Object.entries(error.data?.errors).forEach(([field, errors]) => { 
                formInput[field].error = errors[0];
            });
          }
    
          // Bad Request
          if (error.data?.message != null) {
            dispatch(showErrorToaster(error.data?.message));
          }
        }
    };

    useEffect(() => {
        const cookies = new Cookies();

        if (cookies.get('remember')) {
    
            const rememberVal = cookies.get('remember');
    
            if (rememberVal === 'true') {
                handleFormInputChange('userNameOrEmail', cookies.get('userNameOrEmail'));
                handleFormInputChange('password', cookies.get('password'));
            }
    
            handleRemember(rememberVal === 'true');
        }
    
      // eslint-disable-next-line react-hooks/exhaustive-deps 
      }, []);

    return (
        <>
            <Stack alignItems="center" spacing={2} mb={2}>
                <img src={Logo} alt="Grant Advance" width={matchDownMD ? 200 : 300} height="auto" />
                <Typography
                    color={theme.palette.secondary.main}
                    gutterBottom
                    variant={matchDownMD ? 'h3' : 'h2'}
                >
                    Hi there
                </Typography>
                <Typography
                    variant="caption"
                    fontSize="16px"
                    textAlign={matchDownMD ? 'center' : 'inherit'}
                >
                   {message}
                </Typography>
            </Stack>
            <form onSubmit={handleLogin}>
                <FormControlWrapper
                    name="userNameOrEmail"
                    value={userNameOrEmail.value}
                    label={t('labels.user_name_or_email')}
                    type="text"
                    maxLength="300"
                    error={userNameOrEmail.error}
                    theme={theme}
                    onChange={(element) => handleFormInputChange("userNameOrEmail", element.target.value)}
                />

                <FormControlWrapper
                    name="password"
                    value={password.value}
                    label={t('labels.password')}
                    type="password"
                    maxLength="100"
                    error={password.error}
                    theme={theme}
                    onChange={(element) => handleFormInputChange("password", element.target.value)}
                />

                <CheckboxWrapper 
                    name="checked" 
                    label={t('labels.remember_me')}
                    checked={checked} 
                    onChange={(event) => handleRemember(event.target.checked)}
                />

                <Box sx={{ mt: 2 }}>
                    <LoadingButton
                        disableElevation
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        color="secondary"
                        loading={isLoading}
                    >
                        {t('labels.sign_in')}
                    </LoadingButton>
                </Box>
            </form>
        </>
    )
}

export default LoginForm;